import { CONFIG } from '@config';
import { Flags } from '@constants/flags';
import {
  FeatureFlag,
  useFeatureFlagClient,
  useFlags,
} from '@onevesthq/feature-flags/web';
import { FlagSmith } from '@onevesthq/feature-flags/web/providers/flagsmith';
import { enumValues } from '@utils/enumValues';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { ActiveOrganizationProps } from '../providers/userContextProvider';

export type OrganizationFlagIdentity = {
  id: string;
  parents: string;
  name: string | null;
};

export const featureFlagClient = new FeatureFlag({
  flags: Flags,
  provider: new FlagSmith({
    environmentID: CONFIG.services.flagsmithEnvironmentId,
  }),
  resolveIdentity: ({ identity }: { identity: OrganizationFlagIdentity }) => ({
    identifier: `org:${identity.id}`,
    traits: { parents: identity.parents, name: identity.name },
  }),
});

declare module '@onevesthq/feature-flags/web' {
  // biome-ignore lint/style/useConsistentTypeDefinitions: only interfaces can be augmented
  interface Register {
    client: typeof featureFlagClient;
  }
}

const ALL_FLAG_ENTRIES = enumValues(Flags).map((flag) => ({
  flag,
  options: { default: false },
}));

export const useEnabledFlags = () => {
  const flags = useFlags(ALL_FLAG_ENTRIES);

  return useMemo(
    () => enumValues(Flags).filter((flag) => flags[flag].enabled),
    [flags],
  );
};

const toFlagIdentity = (
  organization: ActiveOrganizationProps,
): OrganizationFlagIdentity | null => {
  if (!organization.id) return null;
  return {
    id: organization.id,
    // Use publicParents instead of parents to get the full inheritance chain
    // publicParents provides public info about parent organizations going up
    // the full inheritance chain to the top level, regardless of user permissions
    parents: (organization.publicParents || [])
      .map((parent) => parent.id)
      .join('/'),
    name: organization.translatedName?.en || null,
  };
};

// Flags load once anonymously, then again for `org:<id>`. Until this
// organization's identity has been set and its load settled, the flags are the
// environment defaults, and route guards must not act on them. A failed load
// still settles (the client keeps the last flags), so an outage does not spin.
//
// Must run outside anything that suspends on flags: a suspended tree never runs
// this effect, so the identity would never be set.
export const useFeatureFlagIdentity = (
  organization: ActiveOrganizationProps,
) => {
  const client = useFeatureFlagClient();
  const snapshot = useSyncExternalStore(
    client.subscribe,
    client.getSnapshot,
    client.getSnapshot,
  );
  const identity = toFlagIdentity(organization);
  const identityKey = JSON.stringify(identity);
  const [appliedKey, setAppliedKey] = useState<string>();

  // biome-ignore lint/correctness/useExhaustiveDependencies: identityKey is the value-equal form of identity
  useEffect(() => {
    client.setIdentity(identity);
    setAppliedKey(identityKey);
  }, [client, identityKey]);

  return (
    identity != null &&
    appliedKey === identityKey &&
    snapshot.status !== 'loading'
  );
};
